/**
 * Image Card Modal - Detailed view of deployed image
 *
 * Tabs:
 * - Overview: Basic metadata, deployment info, usage stats
 * - Build History: Template info, agentic changes timeline, build notes
 * - Packages: Installed Python and system packages
 * - Defaults: Configure provider-wide and instance-specific defaults
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { X, Loader2, AlertCircle, Pencil } from 'lucide-react';
import { ImageDetailsResponse, SetDefaultRequest, AgenticChange, LaunchHistoryResponse, InstanceTypeLaunchSummary, StackEntry, ProviderDefaultsResponse } from '../../types/deployedImages';

interface ImageCardProps {
  imageId: string;
  onClose: () => void;
  onDefaultChanged: () => void;
}

export default function ImageCard({ imageId, onClose, onDefaultChanged }: ImageCardProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [imageDetails, setImageDetails] = useState<ImageDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddInstanceDefault, setShowAddInstanceDefault] = useState(false);
  const [newInstanceType, setNewInstanceType] = useState('');
  const [settingDefault, setSettingDefault] = useState(false);
  const [launchHistory, setLaunchHistory] = useState<InstanceTypeLaunchSummary[]>([]);
  const [launchHistoryLoading, setLaunchHistoryLoading] = useState(false);

  // Stacked defaults
  const [providerStack, setProviderStack] = useState<StackEntry[]>([]);
  const [stackWarnings, setStackWarnings] = useState<string[]>([]);
  const [newPriority, setNewPriority] = useState(0);
  const [editingPriority, setEditingPriority] = useState<number | null>(null);
  const [editPriorityValue, setEditPriorityValue] = useState(0);
  const [catalogInstanceTypes, setCatalogInstanceTypes] = useState<Array<{ id: string; vram: number; name: string }>>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useEffect(() => {
    loadImageDetails();
  }, [imageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load stack + catalog when switching to Defaults tab
  useEffect(() => {
    if (activeTab === 3 && imageDetails) {
      loadProviderStack();
      if (catalogInstanceTypes.length === 0) loadCatalogInstanceTypes();
    }
  }, [activeTab, imageDetails?.image?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImageDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setImageDetails(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load image details');
      console.error('Error loading image details:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLaunchHistory = async () => {
    setLaunchHistoryLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.get<LaunchHistoryResponse>(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}/launch-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLaunchHistory(response.data.instance_types);
    } catch (err) {
      console.error('Error loading launch history:', err);
    } finally {
      setLaunchHistoryLoading(false);
    }
  };

  const loadProviderStack = async () => {
    if (!imageDetails) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.get<ProviderDefaultsResponse>(
        `${getApiUrl()}/api/v1/deployed-images/defaults/${imageDetails.image.provider}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stack = response.data.stack ?? [];
      const gaps = response.data.coverage_gaps ?? [];
      setProviderStack(stack);
      const warnings: string[] = [];
      if (gaps.length > 0) {
        warnings.push(
          `${gaps.length} instance type(s) have no compatible default image`
        );
      }
      const hasUniversal = stack.some(s => s.min_gpu_memory_gb === null);
      if (stack.length > 0 && !hasUniversal) {
        warnings.push('No universal fallback in stack — low-VRAM GPUs will fall through to most recent image');
      }
      setStackWarnings(warnings);
    } catch (err) {
      console.error('Error loading provider stack:', err);
    }
  };

  const loadCatalogInstanceTypes = async () => {
    if (!imageDetails) return;
    setCatalogLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/gpu/discover?provider=${imageDetails.image.provider}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const seen = new Map<string, { id: string; vram: number; name: string }>();
      for (const entry of response.data) {
        if (!seen.has(entry.provider_instance_id)) {
          seen.set(entry.provider_instance_id, {
            id: entry.provider_instance_id,
            vram: entry.gpu_memory_per_device_gb || 0,
            name: entry.gpu_model_name || entry.display_name || entry.provider_instance_id,
          });
        }
      }
      const types = Array.from(seen.values()).sort((a, b) => a.vram - b.vram);
      setCatalogInstanceTypes(types);
    } catch (err) {
      console.error('Error loading GPU catalog:', err);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleAddToStack = async (priority: number) => {
    if (!imageDetails) return;

    setSettingDefault(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const request: SetDefaultRequest = {
        provider: imageDetails.image.provider,
        instance_type: undefined,
        priority,
      };

      await axios.post(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}/set-default`,
        request,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      await Promise.all([loadImageDetails(), loadProviderStack()]);
      onDefaultChanged();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add to stack');
      console.error('Error adding to stack:', err);
    } finally {
      setSettingDefault(false);
    }
  };

  const handleRemoveFromStack = async () => {
    if (!imageDetails) return;

    setSettingDefault(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await axios.delete(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}/unset-default?provider=${imageDetails.image.provider}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await Promise.all([loadImageDetails(), loadProviderStack()]);
      onDefaultChanged();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove from stack');
      console.error('Error removing from stack:', err);
    } finally {
      setSettingDefault(false);
    }
  };

  const handleAddInstanceDefault = async () => {
    if (!imageDetails || !newInstanceType) return;

    setSettingDefault(true);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const request: SetDefaultRequest = {
        provider: imageDetails.image.provider,
        instance_type: newInstanceType,
        priority: 10,
      };

      await axios.post(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}/set-default`,
        request,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      setShowAddInstanceDefault(false);
      setNewInstanceType('');
      await loadImageDetails();
      onDefaultChanged();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add instance default');
      console.error('Error adding instance default:', err);
    } finally {
      setSettingDefault(false);
    }
  };

  const handleRemoveInstanceDefault = async (assignmentId: number, instanceType: string) => {
    if (!imageDetails) return;

    setSettingDefault(true);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await axios.delete(
        `${getApiUrl()}/api/v1/deployed-images/${imageId}/unset-default?provider=${imageDetails.image.provider}&instance_type=${instanceType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await loadImageDetails();
      onDefaultChanged();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove instance default');
      console.error('Error removing instance default:', err);
    } finally {
      setSettingDefault(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card variant="glass" className="p-8 max-w-2xl">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-gray-300">Loading image details...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !imageDetails) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card variant="glass" className="p-6 max-w-2xl">
          <div className="flex items-center gap-2 text-red-300 mb-4">
            <AlertCircle className="w-5 h-5" />
            <p>{error || 'Image not found'}</p>
          </div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </Card>
      </div>
    );
  }

  const { image, build_history, package_manifest, usage_stats } = imageDetails;
  const default_assignments = imageDetails.default_assignments ?? [];

  const isInStack = default_assignments.some(a => a.instance_type === null || a.instance_type === undefined);
  const instanceDefaults = default_assignments.filter(a => a.instance_type != null);
  const minVram = image.min_gpu_memory_gb;

  // Build resolution preview from stack
  const GPU_TIERS = [
    { label: 'T4 / 16GB', vram: 16 },
    { label: 'A10G / 24GB', vram: 24 },
    { label: 'A100 / 40GB', vram: 40 },
    { label: 'A100 / 80GB', vram: 80 },
    { label: 'H100 / 80GB', vram: 80 },
  ];

  const resolveImageForVram = (vram: number): { name: string; isThisImage: boolean } | null => {
    for (const entry of providerStack) {
      const meetsReq = entry.min_gpu_memory_gb === null || entry.min_gpu_memory_gb <= vram;
      if (meetsReq) {
        return { name: entry.image_name, isThisImage: entry.image_id === imageId };
      }
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card variant="glass" className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
        {/* Gradient accent bar */}
        <div className={`h-1.5 ${
          image.deployment_status === 'deployed' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
          image.deployment_status === 'building' ? 'bg-gradient-to-r from-blue-500 to-indigo-600' :
          'bg-gradient-to-r from-gray-500 to-gray-600'
        }`} />

        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-800/50">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white mb-2">{image.image_name}</h2>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <code className="text-gray-400 font-mono">{image.image_id}</code>
              <Badge className={
                image.deployment_status === 'deployed' ? 'bg-green-600/20 text-green-300 border-green-500/30' :
                image.deployment_status === 'building' ? 'bg-blue-600/20 text-blue-300 border-blue-500/30' :
                'bg-gray-600/20 text-gray-300 border-gray-500/30'
              }>
                {image.deployment_status}
              </Badge>
              <Badge className={
                image.validation_status === 'passed' ? 'bg-green-600/20 text-green-300 border-green-500/30' :
                image.validation_status === 'testing' ? 'bg-yellow-600/20 text-yellow-300 border-yellow-500/30' :
                'bg-gray-600/20 text-gray-300 border-gray-500/30'
              }>
                {image.validation_status}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-800/50">
          <nav className="flex px-6">
            {['Overview', 'Build History', 'Packages', 'Defaults', 'Launch History'].map((tab, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setActiveTab(idx);
                  if (idx === 4 && launchHistory.length === 0 && !launchHistoryLoading) {
                    loadLaunchHistory();
                  }
                }}
                className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === idx
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 0 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                    Image Details
                  </h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Provider</dt>
                      <dd className="text-sm text-white">{image.provider.toUpperCase()}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Region</dt>
                      <dd className="text-sm text-white">{image.region}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Architecture</dt>
                      <dd className="text-sm text-white">{image.architecture}</dd>
                    </div>
                    {image.size_gb && (
                      <div>
                        <dt className="text-xs text-gray-500">Size</dt>
                        <dd className="text-sm text-white">{image.size_gb.toFixed(2)} GB</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-xs text-gray-500">Deployed At</dt>
                      <dd className="text-sm text-white">
                        {new Date(image.deployed_at).toLocaleString()}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                    Software Stack
                  </h3>
                  <dl className="space-y-2">
                    {image.python_version && (
                      <div>
                        <dt className="text-xs text-gray-500">Python</dt>
                        <dd className="text-sm text-white">{image.python_version}</dd>
                      </div>
                    )}
                    {image.cuda_version && (
                      <div>
                        <dt className="text-xs text-gray-500">CUDA</dt>
                        <dd className="text-sm text-green-400">{image.cuda_version}</dd>
                      </div>
                    )}
                    {image.driver_version && (
                      <div>
                        <dt className="text-xs text-gray-500">Driver</dt>
                        <dd className="text-sm text-white">{image.driver_version}</dd>
                      </div>
                    )}
                    {image.pytorch_version && (
                      <div>
                        <dt className="text-xs text-gray-500">PyTorch</dt>
                        <dd className="text-sm text-white">{image.pytorch_version}</dd>
                      </div>
                    )}
                    {image.tensorflow_version && (
                      <div>
                        <dt className="text-xs text-gray-500">TensorFlow</dt>
                        <dd className="text-sm text-white">{image.tensorflow_version}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                  Usage Statistics
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-3 hover:bg-gray-900/50 transition-colors">
                    <div className="text-2xl font-bold text-blue-400 tabular-nums">{usage_stats.deploy_count}</div>
                    <div className="text-xs text-gray-400 mt-1">Times Used for Launches</div>
                  </div>
                  {usage_stats.last_used_at && (
                    <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-3 hover:bg-gray-900/50 transition-colors">
                      <div className="text-sm font-medium text-white">
                        {new Date(usage_stats.last_used_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Last Used</div>
                    </div>
                  )}
                  {usage_stats.avg_boot_time_seconds && (
                    <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-3 hover:bg-gray-900/50 transition-colors">
                      <div className="text-xl font-bold text-green-400 tabular-nums">
                        {usage_stats.avg_boot_time_seconds.toFixed(1)}s
                      </div>
                      <div className="text-xs text-gray-400 mt-1">Avg Boot Time</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Build History Tab */}
          {activeTab === 1 && (
            <div className="space-y-4">
              {/* Manual Deployment Notice */}
              {!imageDetails.image.template_id && (
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-400 text-xl">📌</span>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-300 mb-2">
                        Manually Deployed Image
                      </h4>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        This image was deployed manually (not through the Admin Image Builder).
                        Build history, agentic changes, and package manifest are not available.
                        The image is still fully functional and can be set as a default for instance launches.
                      </p>
                      <div className="mt-3 bg-gray-900/50 rounded p-2">
                        <p className="text-xs text-gray-400">
                          <span className="font-medium text-gray-300">Created by:</span> {imageDetails.image.created_by || 'Unknown'}
                          {' • '}
                          <span className="font-medium text-gray-300">Deployed:</span> {new Date(imageDetails.image.deployed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {build_history?.has_build_history ? (
                <>
                  {/* Build Job Info */}
                  <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                      Build Job
                    </h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-xs text-gray-500">Job ID</dt>
                        <dd className="text-sm text-white">#{build_history.build_job?.id}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Status</dt>
                        <dd className={`text-sm font-medium ${
                          build_history.build_job?.status === 'COMPLETED' ? 'text-green-400' : 'text-gray-300'
                        }`}>
                          {build_history.build_job?.status}
                        </dd>
                      </div>
                      {build_history.build_job?.duration_seconds && (
                        <div>
                          <dt className="text-xs text-gray-500">Build Duration</dt>
                          <dd className="text-sm text-white">
                            {(build_history.build_job.duration_seconds / 60).toFixed(1)} minutes
                          </dd>
                        </div>
                      )}
                      {build_history.template && (
                        <div>
                          <dt className="text-xs text-gray-500">Template</dt>
                          <dd className="text-sm text-white">{build_history.template.name}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Agentic Changes Timeline */}
                  {build_history.agentic_timeline && build_history.agentic_timeline.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-blue-300 mb-3 flex items-center gap-2">
                        <span className="text-lg">🔧</span>
                        AI Auto-Fixes Applied ({build_history.agentic_timeline.length})
                      </h3>
                      <p className="text-xs text-gray-400 mb-4">
                        {build_history.agentic_timeline.some((c: AgenticChange) => c.phase === 'packer_build')
                          ? 'AI analyzed issues during both VM testing and Packer build. Suggested fixes are shown below.'
                          : 'The system automatically detected and fixed these issues during pre-flight validation.'}
                      </p>

                      <div className="space-y-3">
                        {build_history.agentic_timeline.map((change: AgenticChange, idx: number) => (
                          <div key={idx} className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                            {/* Header with phase indicator */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  change.phase === 'vm_testing'
                                    ? 'bg-green-900/30 text-green-400'
                                    : 'bg-orange-900/30 text-orange-400'
                                }`}>
                                  {change.phase === 'vm_testing' ? '🧪 VM Testing' : '🔨 Packer Build'}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {change.phase === 'vm_testing'
                                    ? `Command #${(change.command_index ?? 0) + 1}`
                                    : change.provisioner || 'Provisioner'} on {change.provider.toUpperCase()}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {change.model_used}
                              </span>
                            </div>

                            <div className="space-y-2">
                              {/* VM Testing Format */}
                              {change.phase === 'vm_testing' && change.original_command && (
                                <>
                                  <div>
                                    <p className="text-xs text-red-400 font-medium mb-1">❌ Original:</p>
                                    <code className="text-xs text-red-300 bg-red-900/20 px-2 py-1 rounded block break-all">
                                      {change.original_command}
                                    </code>
                                  </div>

                                  <div>
                                    <p className="text-xs text-green-400 font-medium mb-1">✅ Auto-Fixed:</p>
                                    <code className="text-xs text-green-300 bg-green-900/20 px-2 py-1 rounded block break-all">
                                      {change.fixed_command}
                                    </code>
                                  </div>
                                </>
                              )}

                              {/* Packer RCA Format */}
                              {change.phase === 'packer_build' && change.original_error && (
                                <>
                                  <div>
                                    <p className="text-xs text-red-400 font-medium mb-1">❌ Build Error:</p>
                                    <code className="text-xs text-red-300 bg-red-900/20 px-2 py-1 rounded block break-all">
                                      {change.original_error}
                                    </code>
                                  </div>

                                  <div>
                                    <p className="text-xs text-blue-400 font-medium mb-1 flex items-center gap-1">
                                      💡 AI Suggested Fix
                                      {!change.auto_applied && (
                                        <span className="text-xs text-yellow-400 font-normal">(For review)</span>
                                      )}
                                    </p>
                                    <code className="text-xs text-blue-300 bg-blue-900/20 px-2 py-1 rounded block break-all">
                                      {change.ai_suggested_fix}
                                    </code>
                                  </div>
                                </>
                              )}

                              <div className="bg-blue-900/20 border-l-4 border-blue-500 p-2 rounded">
                                <p className="text-xs text-gray-400 leading-relaxed">
                                  <span className="font-medium text-blue-300">Why:</span> {change.reason}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Build Notes */}
                  {build_history.build_notes && (
                    <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                        Build Notes
                      </h3>
                      <p className="text-sm text-gray-400 mb-2">{build_history.build_notes.notes}</p>
                      {build_history.build_notes.warnings && build_history.build_notes.warnings.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-yellow-400 font-medium mb-1">Warnings:</p>
                          {build_history.build_notes.warnings.map((warning, idx) => (
                            <p key={idx} className="text-xs text-gray-400">{warning}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No build history available for this image</p>
                </div>
              )}
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === 2 && (
            <div className="space-y-4">
              {package_manifest && Object.keys(package_manifest.python_packages || {}).length > 0 ? (
                <>
                  {/* Categorized view when manifest has category lists */}
                  {(package_manifest.rapids_packages?.length ?? 0) > 0 && (() => {
                    const rapidsSet = new Set(package_manifest.rapids_packages as string[]);
                    const rapidsEntries = Object.entries(package_manifest.python_packages)
                      .filter(([pkg]) => rapidsSet.has(pkg));
                    return rapidsEntries.length > 0 ? (
                      <div className="bg-gray-900/30 border border-purple-500/30 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-purple-300 mb-3 uppercase tracking-wide">
                          RAPIDS Packages ({rapidsEntries.length})
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {rapidsEntries.map(([pkg, version]) => (
                            <div key={pkg} className="flex justify-between items-center text-xs bg-purple-900/20 px-2 py-1.5 rounded border border-purple-500/10">
                              <code className="text-purple-200">{pkg}</code>
                              <span className="text-purple-400">{String(version)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {(package_manifest.baseline_packages?.length ?? 0) > 0 && (() => {
                    const baselineSet = new Set(package_manifest.baseline_packages as string[]);
                    const baselineEntries = Object.entries(package_manifest.python_packages)
                      .filter(([pkg]) => baselineSet.has(pkg));
                    return baselineEntries.length > 0 ? (
                      <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                          Scientific Stack ({baselineEntries.length})
                        </h3>
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {baselineEntries.map(([pkg, version]) => (
                            <div key={pkg} className="flex justify-between items-center text-xs bg-gray-800/50 px-2 py-1.5 rounded">
                              <code className="text-gray-300">{pkg}</code>
                              <span className="text-gray-500">{String(version)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {(package_manifest.template_packages?.length ?? 0) > 0 && (() => {
                    const templateSet = new Set(package_manifest.template_packages as string[]);
                    const templateEntries = Object.entries(package_manifest.python_packages)
                      .filter(([pkg]) => templateSet.has(pkg));
                    return templateEntries.length > 0 ? (
                      <div className="bg-gray-900/30 border border-blue-500/30 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-blue-300 mb-3 uppercase tracking-wide">
                          Template Packages ({templateEntries.length})
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {templateEntries.map(([pkg, version]) => (
                            <div key={pkg} className="flex justify-between items-center text-xs bg-blue-900/20 px-2 py-1.5 rounded border border-blue-500/10">
                              <code className="text-blue-200">{pkg}</code>
                              <span className="text-blue-400">{String(version)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Flat fallback for old manifests without category lists */}
                  {!package_manifest.rapids_packages && !package_manifest.baseline_packages && (
                    <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                        Python Packages ({Object.keys(package_manifest.python_packages).length})
                      </h3>
                      <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                        {Object.entries(package_manifest.python_packages).map(([pkg, version]) => (
                          <div key={pkg} className="flex justify-between items-center text-xs bg-gray-800/50 px-2 py-1.5 rounded">
                            <code className="text-gray-300">{pkg}</code>
                            <span className="text-gray-500">{String(version)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* System Packages */}
                  {package_manifest.system_packages && Object.keys(package_manifest.system_packages).length > 0 && (
                    <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                        System Packages ({Object.keys(package_manifest.system_packages).length})
                      </h3>
                      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                        {Object.entries(package_manifest.system_packages).map(([pkg, version]) => (
                          <div key={pkg} className="flex justify-between items-center text-xs bg-gray-800/50 px-2 py-1.5 rounded">
                            <code className="text-gray-300">{pkg}</code>
                            <span className="text-gray-500">{String(version)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No package manifest available</p>
                </div>
              )}
            </div>
          )}

          {/* Defaults Tab */}
          {activeTab === 3 && (
            <div className="space-y-6">
              {/* Warnings */}
              {stackWarnings.length > 0 && (
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                  {stackWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-yellow-300">{w}</p>
                  ))}
                </div>
              )}

              {/* VRAM Badge */}
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                  minVram ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30' : 'bg-green-600/20 text-green-300 border border-green-500/30'
                }`}>
                  {minVram ? `Requires ${minVram}GB+ VRAM` : 'Universal (any GPU)'}
                </span>
              </div>

              {/* Provider-Wide Default Stack */}
              <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                  {image.provider.toUpperCase()} Default Stack
                </h3>
                <p className="text-xs text-gray-400 mb-4">
                  Multiple images can share the provider-wide default slot. At launch, the highest-priority
                  image compatible with the GPU's VRAM is selected.
                </p>

                {/* Full stack view */}
                {providerStack.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {providerStack.map((entry, idx) => {
                      const isThisImage = entry.image_id === imageId;
                      return (
                        <div
                          key={entry.image_id}
                          className={`flex justify-between items-center rounded p-3 border transition-colors ${
                            isThisImage
                              ? 'bg-blue-900/30 border-blue-500/40'
                              : 'bg-gray-800/50 border-gray-600 hover:bg-gray-800/70'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 font-mono w-6 text-right">
                              #{idx + 1}
                            </span>
                            <div>
                              <span className={`text-sm font-medium ${isThisImage ? 'text-blue-300' : 'text-white'}`}>
                                {entry.image_name}
                              </span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-400">
                                  Priority: {entry.priority}{idx === 0 && ' (higher = top)'}
                                </span>
                                <span className={`px-1.5 py-0.5 text-xs rounded ${
                                  entry.min_gpu_memory_gb
                                    ? 'bg-purple-900/30 text-purple-300'
                                    : 'bg-green-900/30 text-green-300'
                                }`}>
                                  {entry.min_gpu_memory_gb ? `${entry.min_gpu_memory_gb}GB+` : 'Any GPU'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isThisImage && (
                            <div className="flex items-center gap-2">
                              {editingPriority !== null ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={editPriorityValue}
                                    onChange={(e) => setEditPriorityValue(parseInt(e.target.value) || 0)}
                                    className="w-16 px-2 py-1 bg-gray-700 text-white rounded text-xs border border-gray-600"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      handleAddToStack(editPriorityValue);
                                      setEditingPriority(null);
                                    }}
                                    disabled={settingDefault}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setEditingPriority(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setEditingPriority(entry.priority);
                                      setEditPriorityValue(entry.priority);
                                    }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                    Priority
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleRemoveFromStack}
                                    disabled={settingDefault}
                                  >
                                    Remove
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4 mb-4">
                    No provider-wide defaults configured
                  </p>
                )}

                {/* Add to stack button (only if not already in stack) */}
                {!isInStack && (
                  <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Priority:</span>
                      <input
                        type="number"
                        value={newPriority}
                        onChange={(e) => setNewPriority(parseInt(e.target.value) || 0)}
                        className="w-20 px-2 py-1.5 bg-gray-700 text-white rounded text-sm border border-gray-600 focus:outline-none focus:border-blue-500"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddToStack(newPriority)}
                        disabled={settingDefault}
                      >
                        {settingDefault ? 'Adding...' : 'Add to Default Stack'}
                      </Button>
                    </div>
                    <span className="text-xs text-gray-500">Higher number = checked first at launch</span>
                  </div>
                )}
              </div>

              {/* Instance-Specific Defaults */}
              <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                    Instance-Specific Defaults
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowAddInstanceDefault(true);
                      if (catalogInstanceTypes.length === 0) loadCatalogInstanceTypes();
                    }}
                  >
                    + Add Instance Type
                  </Button>
                </div>

                <p className="text-xs text-gray-400 mb-4">
                  Override the provider-wide stack for specific instance types.
                </p>

                {showAddInstanceDefault && (
                  <div className="bg-gray-800/50 border border-gray-600 rounded p-3 mb-4">
                    <div className="flex gap-2">
                      {catalogLoading ? (
                        <div className="flex-1 px-3 py-2 bg-gray-700 text-gray-400 rounded text-sm border border-gray-600">
                          Loading instance types...
                        </div>
                      ) : (
                        <select
                          value={newInstanceType}
                          onChange={(e) => setNewInstanceType(e.target.value)}
                          className="flex-1 px-3 py-2 bg-gray-700 text-white rounded text-sm border border-gray-600 focus:outline-none focus:border-blue-500"
                        >
                          <option value="">Select instance type...</option>
                          {catalogInstanceTypes
                            .filter(t => !minVram || t.vram >= minVram)
                            .map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.id} ({t.vram}GB - {t.name})
                              </option>
                            ))
                          }
                        </select>
                      )}
                      <Button
                        size="sm"
                        onClick={handleAddInstanceDefault}
                        disabled={!newInstanceType || settingDefault}
                      >
                        Add
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setShowAddInstanceDefault(false);
                          setNewInstanceType('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {instanceDefaults.length > 0 ? (
                  <div className="space-y-2">
                    {instanceDefaults.map((def) => (
                      <div
                        key={def.id}
                        className="flex justify-between items-center bg-gray-800/50 border border-gray-600 rounded p-3"
                      >
                        <div className="flex-1">
                          <code className="text-sm text-white font-mono">{def.instance_type}</code>
                          <p className="text-xs text-gray-400 mt-1">
                            Priority: {def.priority} &middot; Created {new Date(def.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveInstanceDefault(def.id, def.instance_type!)}
                          disabled={settingDefault}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-6">
                    No instance-specific defaults configured
                  </p>
                )}
              </div>

              {/* Resolution Preview */}
              {providerStack.length > 0 && (
                <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">
                    Resolution Preview
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Which image each GPU tier receives from the current stack:
                  </p>
                  <div className="space-y-1.5">
                    {GPU_TIERS.map((tier) => {
                      const resolved = resolveImageForVram(tier.vram);
                      return (
                        <div key={tier.label} className="flex items-center gap-3 text-xs">
                          <span className="text-gray-400 w-28 font-mono">{tier.label}</span>
                          <span className="text-gray-600">&rarr;</span>
                          {resolved ? (
                            <span className={resolved.isThisImage ? 'text-blue-300 font-medium' : 'text-gray-300'}>
                              {resolved.name}
                              {resolved.isThisImage && ' (this image)'}
                            </span>
                          ) : (
                            <span className="text-yellow-400">Fallback (most recent validated)</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Info Panel */}
              <div className="bg-blue-900/10 border-l-4 border-blue-500 p-3">
                <p className="text-xs text-gray-400 leading-relaxed">
                  <strong className="text-blue-300">How stacking works:</strong> At launch, the system checks
                  instance-specific defaults first, then walks the provider-wide stack from highest to lowest priority.
                  The first image whose VRAM requirement is met by the GPU gets selected. A universal fallback
                  (any GPU) at the bottom of the stack ensures every instance type is covered.
                </p>
              </div>
            </div>
          )}

          {/* Launch History Tab */}
          {activeTab === 4 && (
            <div className="space-y-4">
              {launchHistoryLoading ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-400 text-sm">Loading launch history...</p>
                </div>
              ) : launchHistory.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">No launches recorded yet</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Launch records will appear here once instances are launched using this image.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-900/30 border border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Instance Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Launches</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Success</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Failed</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Last Success</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Last Failure</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {launchHistory.map((entry) => {
                        const successRate = entry.total_launches > 0
                          ? Math.round((entry.successful / entry.total_launches) * 100)
                          : 0;
                        return (
                          <tr key={entry.instance_type} className="hover:bg-gray-700/20">
                            <td className="px-4 py-3">
                              <code className="text-sm text-white font-mono">{entry.instance_type}</code>
                              <div className="text-xs text-gray-500">{entry.provider.toUpperCase()}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-white font-medium">{entry.total_launches}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-green-400 font-medium">{entry.successful}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  successRate === 100 ? 'bg-green-900/30 text-green-400' :
                                  successRate >= 80 ? 'bg-yellow-900/30 text-yellow-400' :
                                  'bg-red-900/30 text-red-400'
                                }`}>
                                  {successRate}%
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-medium ${entry.failed > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                {entry.failed}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {entry.last_success ? (
                                <span className="text-xs text-gray-300">
                                  {new Date(entry.last_success).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">&mdash;</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {entry.last_failure ? (
                                <div>
                                  <span className="text-xs text-gray-300">
                                    {new Date(entry.last_failure).toLocaleDateString()}
                                  </span>
                                  {entry.last_failure_reason && (
                                    <p className="text-xs text-red-400 mt-0.5 truncate max-w-48" title={entry.last_failure_reason}>
                                      {entry.last_failure_reason}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">&mdash;</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-800/50 p-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    </div>
  );
}
